import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import Layout from '../components/Layout';
import { appAPI, pomodoroAPI, Application, PomodoroListQuery, PomodoroPaginatedResponse } from '../services/api';
import './ApplicationDetails.css';

const ApplicationDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [application, setApplication] = useState<Application | null>(null);
  const [pomodoros, setPomodoros] = useState<PomodoroPaginatedResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingPomodoros, setIsLoadingPomodoros] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [filters, setFilters] = useState<PomodoroListQuery>({
    page: 1,
    page_size: 10,
  });

  // Mock pomodoro dialog states
  const [showMockDialog, setShowMockDialog] = useState(false);
  const [mockDescription, setMockDescription] = useState('');
  const [isSubmittingMock, setIsSubmittingMock] = useState(false);

  const appId = parseInt(id || '0');

  useEffect(() => {
    if (isNaN(appId)) {
      showToast('Invalid application ID', 'error');
      navigate('/dashboard');
      return;
    }

    fetchApplication();
    fetchPomodoros();
  }, [appId]);

  const fetchApplication = async () => {
    try {
      setIsLoading(true);
      const app = await appAPI.getApplication(appId);
      setApplication(app);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch application details';
      showToast(errorMessage, 'error');
      navigate('/dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPomodoros = async (query?: PomodoroListQuery) => {
    try {
      setIsLoadingPomodoros(true);
      const queryParams = {
        page: query?.page || currentPage,
        page_size: pageSize,
        ...query,
      };

      const data = await pomodoroAPI.getPomodoros(queryParams);
      setPomodoros(data);
      setFilters(queryParams);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch pomodoros';
      showToast(errorMessage, 'error');
      setPomodoros(null);
    } finally {
      setIsLoadingPomodoros(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    fetchPomodoros({ ...filters, page: newPage });
  };

  const handleFilterChange = (newFilters: PomodoroListQuery) => {
    setCurrentPage(1);
    fetchPomodoros({ ...newFilters, page: 1 });
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  // Mock pomodoro functions
  const handleMockPomodoro = () => {
    setMockDescription('');
    setShowMockDialog(true);
  };

  const handleCancelMock = () => {
    setShowMockDialog(false);
    setMockDescription('');
  };

  const handleSubmitMock = async () => {
    if (!mockDescription.trim()) {
      showToast('Please enter a description for the pomodoro session', 'error');
      return;
    }

    setIsSubmittingMock(true);
    try {
      await pomodoroAPI.mockPomodoro({
        application_id: appId,
        description: mockDescription.trim()
      });

      showToast(`Mock pomodoro created successfully for ${application?.name}!`, 'success');
      setShowMockDialog(false);
      setMockDescription('');
      // Refresh pomodoros list
      fetchPomodoros(filters);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to create mock pomodoro';
      showToast(errorMessage, 'error');
    } finally {
      setIsSubmittingMock(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (startTime: string, endTime: string) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMs = end.getTime() - start.getTime();
    const minutes = Math.floor(durationMs / (1000 * 60));
    return `${minutes} minutes`;
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="application-details-container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading application details...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!application) {
    return (
      <Layout>
        <div className="application-details-container">
          <div className="error-state">
            <p>Application not found</p>
            <button onClick={handleBack} className="back-button">Back to Dashboard</button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="application-details-container">
        <div className="details-header">
          <button onClick={handleBack} className="back-button">
            ← Back to Dashboard
          </button>
          <div className="app-info">
            <h1>{application.name}</h1>
            <p className="app-description">{application.description || 'No description provided'}</p>
            <div className="app-meta">
              <span>App ID: <code>{application.app_id}</code></span>
              <span>Created: {formatDate(application.created_at)}</span>
              <span>Status: <span className={`status ${application.is_active ? 'active' : 'inactive'}`}>
                {application.is_active ? 'Active' : 'Inactive'}
              </span></span>
            </div>
          </div>
          <button onClick={handleMockPomodoro} className="mock-pomodoro-button">
            Create Mock Pomodoro
          </button>
        </div>

        <div className="pomodoros-section">
          <div className="section-header">
            <h2>Pomodoro Sessions</h2>
            <div className="filters">
              <input
                type="date"
                placeholder="From date"
                value={filters.date_from || ''}
                onChange={(e) => handleFilterChange({ ...filters, date_from: e.target.value })}
                className="filter-input"
              />
              <input
                type="date"
                placeholder="To date"
                value={filters.date_to || ''}
                onChange={(e) => handleFilterChange({ ...filters, date_to: e.target.value })}
                className="filter-input"
              />
              <input
                type="text"
                placeholder="Search descriptions..."
                value={filters.keyword || ''}
                onChange={(e) => handleFilterChange({ ...filters, keyword: e.target.value })}
                className="filter-input"
              />
            </div>
          </div>

          {isLoadingPomodoros ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading pomodoros...</p>
            </div>
          ) : pomodoros?.items === null ? (
            <div className="empty-state">
              <h3>No Pomodoro Sessions Found</h3>
              <p>Create your first pomodoro session to start tracking your productivity.</p>
              <button onClick={handleMockPomodoro} className="mock-pomodoro-button">
                Create Mock Pomodoro
              </button>
            </div>
          ) : pomodoros?.items.length === 0 ? (
            <div className="empty-state">
              <h3>No Pomodoro Sessions Found</h3>
              <p>Create your first pomodoro session to start tracking your productivity.</p>
              <button onClick={handleMockPomodoro} className="mock-pomodoro-button">
                Create Mock Pomodoro
              </button>
            </div>
          ) : (
            <div className="pomodoros-content">
              <div className="pomodoros-table-container">
                <table className="pomodoros-table">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Duration</th>
                      <th>Start Time</th>
                      <th>End Time</th>
                      <th>Source</th>
                      <th>Client</th>
                      <th>IP Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(pomodoros?.items || []).map((pomodoro) => (
                      <tr key={pomodoro.id}>
                        <td className="description-cell">
                          <span className="description-text">
                            {pomodoro.description || 'Untitled Session'}
                          </span>
                        </td>
                        <td className="duration-cell">
                          <span className="duration-badge">
                            {formatDuration(pomodoro.start_time, pomodoro.end_time)}
                          </span>
                        </td>
                        <td className="time-cell">{formatDate(pomodoro.start_time)}</td>
                        <td className="time-cell">{formatDate(pomodoro.end_time)}</td>
                        <td className="source-cell">{pomodoro.source}</td>
                        <td className="client-cell">{pomodoro.client}</td>
                        <td className="ip-cell">{pomodoro.ip_address}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pomodoros && pomodoros.total_pages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className="pagination-button"
                  >
                    Previous
                  </button>
                  <span className="page-info">
                    Page {currentPage} of {pomodoros.total_pages} ({pomodoros.total} total)
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= pomodoros.total_pages}
                    className="pagination-button"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mock Pomodoro Dialog */}
        {showMockDialog && (
          <div className="dialog-overlay">
            <div className="dialog-card">
              <div className="dialog-header">
                <h3>Create Mock Pomodoro</h3>
                <button
                  className="dialog-close-button"
                  onClick={handleCancelMock}
                  disabled={isSubmittingMock}
                >
                  ×
                </button>
              </div>
              <div className="dialog-content">
                <p>Create a mock pomodoro session for <strong>{application.name}</strong></p>
                <p className="dialog-description">
                  This will create a pomodoro session that started 25 minutes ago and ended just now.
                </p>
                <div className="form-group">
                  <label htmlFor="mock-description">Description *</label>
                  <textarea
                    id="mock-description"
                    value={mockDescription}
                    onChange={(e) => setMockDescription(e.target.value)}
                    placeholder="What did you work on during this pomodoro session?"
                    rows={3}
                    disabled={isSubmittingMock}
                  />
                </div>
              </div>
              <div className="dialog-actions">
                <button
                  onClick={handleCancelMock}
                  disabled={isSubmittingMock}
                  className="cancel-button"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitMock}
                  disabled={isSubmittingMock || !mockDescription.trim()}
                  className="submit-button"
                >
                  {isSubmittingMock ? 'Creating...' : 'Create Mock Pomodoro'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ApplicationDetails;